export const CHECKOUT_URL = 'https://hipertension-en.impultienda.ar/checkout/'

export const stats = [
  { value: '+300', label: 'Simplified clinical decisions' },
  { value: '+2,000', label: 'Physicians already using the route' },
  { value: '1,800+', label: 'Hours saved each year reviewing guidelines' },
  { value: '99%', label: 'Would recommend it to a colleague' },
]

export const beforeItems = [
  'Reviewing multiple guidelines to answer a single clinical question',
  'Wondering whether to start monotherapy or combination therapy',
  'Feeling like every guideline recommends something different',
  'Improvising when the patient fails to reach treatment goals',
  'Worrying about making mistakes in complex patients',
  'Spending more time searching for answers than treating patients',
]

export const afterItems = [
  'Follow a clear route and make decisions in minutes',
  'Choose the appropriate treatment with a step-by-step algorithm',
  'Apply one clear, evidence-based clinical framework',
  'Know when to adjust, intensify, or refer',
  'Manage diabetes, CKD, pregnancy and other complex scenarios with confidence',
  'Have the information organized and ready to use during the consultation',
]

export const painPoints = [
  {
    emoji: '😰',
    text: 'You have a patient in front of you and you’re unsure whether to intensify treatment, switch an ACE inhibitor, add a diuretic, or move to a third medication.',
  },
  {
    emoji: '⏱️',
    text: 'You spend far too much time reviewing guidelines to answer a question that should take minutes — not hours.',
  },
  {
    emoji: '🤷',
    text: 'A patient presents with hypertension, diabetes and CKD, and you need to cross-check multiple recommendations before deciding what to do.',
  },
  {
    emoji: '💸',
    text: 'You’re not always sure when a case can be managed in primary care and when referral is the safer choice.',
  },
  {
    emoji: '😓',
    text: 'Your follow-ups lack a consistent structure, making it harder to know whether treatment is actually working.',
  },
  {
    emoji: '😩',
    text: 'A patient presents with severely elevated blood pressure and you don’t have the exact clinical pathway immediately available.',
  },
]

export const contentBlocks = [
  {
    emoji: '📘',
    title: '6 Clinical Volumes',
    text: 'Cover the complete hypertension management process — from diagnostic evaluation to long-term follow-up.',
  },
  {
    emoji: '🩺',
    title: '300+ Clinical Protocols',
    text: 'Handle common clinical scenarios with visual algorithms, decision tables and ready-to-use clinical pathways.',
  },
  {
    emoji: '📊',
    title: 'Clinical Decision Algorithms',
    text: 'Know when to initiate treatment, intensify therapy, or change your strategy using clear, evidence-based criteria.',
  },
  {
    emoji: '⚡',
    title: 'Complex Patient Pathways',
    text: 'Dedicated clinical routes for diabetes, chronic kidney disease, older adults, pregnancy and resistant hypertension.',
  },
  {
    emoji: '📋',
    title: 'Clinical Reference Tables',
    text: 'Quickly review medication doses, therapeutic options and treatment schemes without searching through multiple resources.',
  },
  {
    emoji: '🎯',
    title: 'Clinical Follow-Up System',
    text: 'Use a structured process to document, monitor and adjust treatment at every follow-up visit.',
  },
]

export const deliveryItems = [
  { emoji: '📘', text: 'The 6 Clinical Volumes' },
  { emoji: '🩺', text: '300+ Clinical Protocols' },
  { emoji: '⚡', text: 'Rapid Decision Algorithms' },
  { emoji: '🎯', text: 'Complex Patient Pathways' },
  { emoji: '📋', text: 'Clinical Follow-Up System' },
  { emoji: '🎁', text: '5 Exclusive Bonuses' },
]

export const bonuses = [
  {
    number: 1,
    title: 'The Combination Atlas',
    text: 'Choose the right antihypertensive combination in seconds with a visual algorithm designed to help you avoid common prescribing errors.',
    value: '$27 USD',
    image:
      'https://cdn.magicpatterns.com/uploads/chpSKyiQCnhGSeNTN8N2GM/BONUS_1_HIPERTENSION-EN.png',
    alt: 'Bonus 1: The Combination Atlas — book, tablet, phone and quick-reference cards with first-line combination algorithms',
  },
  {
    number: 2,
    title: 'Hypertension Clinical Prescription Guide',
    text: '50 ready-to-use treatment schemes organized by patient profile, so you don’t have to start from scratch during every consultation.',
    value: '$37 USD',
    image:
      'https://cdn.magicpatterns.com/uploads/cPiWDHboNxmCrCwqxMjdbE/BONUS_2_HIPERTENSION-EN.png',
    alt: 'Bonus 2: Hypertension Clinical Prescription Guide — book, tablet, phone and treatment-scheme cards by patient profile',
  },
  {
    number: 3,
    title: 'Secondary Hypertension Master Checklist',
    text: 'Identify the major secondary causes of hypertension before unnecessarily escalating treatment.',
    value: '$27 USD',
    image:
      'https://cdn.magicpatterns.com/uploads/kqbRtfL7pv1zUcveLfdf82/BONUS_3_HIPERTENSION-EN.png',
    alt: 'Bonus 3: Secondary Hypertension Master Checklist — book, tablet, phone and screening flowchart cards',
  },
  {
    number: 4,
    title: 'Pro-Patient Education Kit',
    text: 'Professional patient-facing materials designed to improve adherence and reduce the time you spend repeating the same recommendations.',
    value: '$20 USD',
    image:
      'https://cdn.magicpatterns.com/uploads/1tQiGJzCGMe4LMDvpY8syc/BONUS_4_HIPERTENSION-EN.png',
    alt: 'Bonus 4: Pro-Patient Education Kit — book, tablet, phone and patient handout cards',
  },
  {
    number: 5,
    title: 'MAPA & AMPA Master Guide',
    text: 'Interpret ambulatory and home blood-pressure readings with clear criteria, practical examples and clinical decision pathways.',
    value: '$37 USD',
    image:
      'https://cdn.magicpatterns.com/uploads/jhxXwMrpfT5sU7VRruj6RH/BONUS_5_HIPERTENSION-EN.png',
    alt: 'Bonus 5: MAPA & AMPA Master Guide — book, spiral guide, tablet, phone and interpretation cards',
  },
]

export const testimonials = [
  {
    quote:
      'I stopped opening three different guidelines during every consultation. Now I follow the route, make the decision in minutes, and my patients notice the confidence when I explain their treatment.',
    name: 'Dr. Andrés Villalba',
    role: 'Internal Medicine · Bogotá, Colombia',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e3d9cad0-1c44-4cad-b956-1d14e20250b4.jpg',
  },
  {
    quote:
      'The combination algorithm solved the question that used to slow me down the most. I can prescribe with a clear framework instead of second-guessing every decision.',
    name: 'Dr. Marcela Ruiz',
    role: 'Family Medicine · Mexico City',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/9e0bd760-8e6e-440b-ae0d-b0eb1b5ff2ec.jpg',
  },
  {
    quote:
      'During on-call shifts, I don’t have time to read 200 pages. I open the route, find the clinical scenario, and move forward. It’s worth far more than the price.',
    name: 'Dr. Julián Espinoza',
    role: 'Internal Medicine Resident · Lima, Peru',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/3d4dcaae-8825-4e54-af00-5d61f3971c56.jpg',
  },
  {
    quote:
      'What I value most are the pathways for complex patients. Diabetes, CKD and pregnancy are organized around one clear, updated clinical framework.',
    name: 'Dr. Carmen Ortiz',
    role: 'Cardiology · Santiago, Chile',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/d1f9b1b6-ec08-4149-bd63-3d71d01554e8.jpg',
  },
]

export const faqs = [
  {
    q: 'What makes Hypertension Route different from a traditional clinical guideline?',
    a: 'Guidelines give you the full body of available evidence. Hypertension Route organizes that evidence into a practical clinical pathway, so you can make decisions in minutes during a consultation instead of reviewing hundreds of pages every time a question comes up.',
  },
  {
    q: 'Will I receive the material immediately?',
    a: 'Yes. As soon as your payment is confirmed, you’ll receive immediate access to download the complete system and all five bonuses in PDF format. You can use them on your computer, tablet or smartphone.',
  },
  {
    q: 'Is it useful if I already know the ESC, ACC/AHA or ISH guidelines?',
    a: 'Yes. Hypertension Route is not designed to replace clinical guidelines. It’s designed to help you apply their recommendations faster and more practically when you have a real patient in front of you.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. You pay once and receive lifetime access. There are no subscriptions, recurring charges or monthly payments.',
  },
  {
    q: 'Is Hypertension Route useful for residents?',
    a: 'Yes. The system is designed for physicians and medical residents who need a practical way to navigate hypertension-related decisions during consultations, rotations and on-call shifts.',
  },
]

export const purchaseNotifications = [
  { name: 'Dr. Valentina Rojas', place: 'Bogotá, Colombia' },
  { name: 'Dr. Miguel Fernández', place: 'Madrid, Spain' },
  { name: 'Dr. Laura Gutiérrez', place: 'Guadalajara, Mexico' },
  { name: 'Dr. Sebastián Ramírez', place: 'Buenos Aires, Argentina' },
  { name: 'Dr. Camila Herrera', place: 'Santiago, Chile' },
  { name: 'Dr. José Manuel Peña', place: 'Lima, Peru' },
  { name: 'Dr. Andrea Morales', place: 'San José, Costa Rica' },
  { name: 'Dr. Ricardo Salazar', place: 'Quito, Ecuador' },
  { name: 'Dr. Paola Mendoza', place: 'Montevideo, Uruguay' },
  { name: 'Dr. Hugo Castillo', place: 'Asunción, Paraguay' },
  { name: 'Dr. Daniela Suárez', place: 'Caracas, Venezuela' },
  { name: 'Dr. Emilio Navarro', place: 'Barcelona, Spain' },
]
